// web/src/pages/CompRedemptionLanding.tsx
//
// /redeem-comp?code=BREW-XXXXXXXX — deep-link landing reached from email
// or push notification. Validates the code against comp_offers (RLS
// requires the customer to be signed in via supabase JWT) and renders a
// state-aware UI: pending → show code, redeemed → confirmation, expired
// → expiry note, unknown → invalid-code note.

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/authStore';
import { restoreSupabaseSession } from '../services/supabase-auth';

type CompStatus = 'pending' | 'redeemed' | 'expired';

interface CompOfferRow {
  id: string;
  code: string;
  status: CompStatus;
  expires_at: string;
  redeemed_at: string | null;
  shops?: { name: string | null } | null;
}

type ResolveState =
  | { kind: 'loading' }
  | { kind: 'needs-login' }
  | { kind: 'no-code' }
  | { kind: 'not-found' }
  | { kind: 'expired'; shopName: string | null; expiresAt: string }
  | { kind: 'redeemed'; shopName: string | null; redeemedAt: string | null }
  | { kind: 'valid'; offer: CompOfferRow };

export function CompRedemptionLanding() {
  const [params] = useSearchParams();
  const code = params.get('code')?.trim() || '';
  const { userAddress } = useAuthStore();
  const [state, setState] = useState<ResolveState>({ kind: 'loading' });

  useEffect(() => {
    if (!code) {
      setState({ kind: 'no-code' });
      return;
    }
    let cancelled = false;
    (async () => {
      // RLS on comp_offers requires the customer JWT to match customer_address.
      // Without an auth'd session we can't read the row even if it exists —
      // surface that as needs-login rather than a misleading not-found.
      if (!userAddress) {
        setState({ kind: 'needs-login' });
        return;
      }
      try {
        await restoreSupabaseSession(userAddress);
        if (cancelled) return;
        const { data, error } = await supabase
          .from('comp_offers')
          .select('id, code, status, expires_at, redeemed_at, shops(name)')
          .eq('code', code)
          .maybeSingle<CompOfferRow>();
        if (cancelled) return;
        if (error || !data) {
          setState({ kind: 'not-found' });
          return;
        }
        const shopName = data.shops?.name ?? null;
        if (data.status === 'redeemed') {
          setState({ kind: 'redeemed', shopName, redeemedAt: data.redeemed_at });
          return;
        }
        if (new Date(data.expires_at).getTime() <= Date.now()) {
          setState({ kind: 'expired', shopName, expiresAt: data.expires_at });
          return;
        }
        if (data.status === 'pending') {
          setState({ kind: 'valid', offer: data });
          return;
        }
        // Any other status (e.g. cancelled if added later) — treat as invalid.
        setState({ kind: 'not-found' });
      } catch {
        if (!cancelled) setState({ kind: 'not-found' });
      }
    })();
    return () => { cancelled = true; };
  }, [code, userAddress]);

  return (
    <main className="min-h-dvh bg-background text-primary flex items-center justify-center p-4 md:p-8 safe-shell">
      <div className="pixel-corners bg-white p-5 sm:p-8 max-w-md w-full text-center">
        {renderBody(state)}
      </div>
    </main>
  );
}

function renderBody(state: ResolveState) {
  if (state.kind === 'loading') {
    return (
      <>
        <h1 className="font-heading text-xl text-coral mb-3">CHECKING CODE…</h1>
        <p className="font-body text-sm text-body-grey">Hang on while we look this up.</p>
      </>
    );
  }
  if (state.kind === 'no-code') {
    return (
      <>
        <h1 className="font-heading text-2xl text-primary mb-3">NO CODE</h1>
        <p className="font-body text-sm text-body-grey">
          This link is missing a comp code. Check your email for the original message.
        </p>
      </>
    );
  }
  if (state.kind === 'needs-login') {
    return (
      <>
        <h1 className="font-heading text-2xl text-coral mb-3">SIGN IN TO CONTINUE</h1>
        <p className="font-body text-sm text-body-grey mb-4">
          Sign in with the Google account this comp was sent to. We'll verify the code and show it to your barista.
        </p>
        <a
          href="/"
          className="inline-block px-4 py-2 border-2 border-coral text-coral font-heading text-xs hover:bg-coral/[.08]"
        >
          SIGN IN →
        </a>
      </>
    );
  }
  if (state.kind === 'not-found') {
    return (
      <>
        <h1 className="font-heading text-2xl text-error mb-3">CODE NOT FOUND</h1>
        <p className="font-body text-sm text-body-grey">
          We can't find this comp on your account. Make sure you're signed in with the same Google account the comp was sent to, then refresh.
        </p>
      </>
    );
  }
  // Voucher dates use long-form month so 02/06 doesn't get read as Feb-6 vs 2-Jun.
  const formatDay = (iso: string) =>
    new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

  if (state.kind === 'redeemed') {
    const when = state.redeemedAt ? formatDay(state.redeemedAt) : null;
    return (
      <>
        <h1 className="font-heading text-2xl text-body-grey mb-3">ALREADY USED</h1>
        <p className="font-body text-sm text-body-grey">
          This free coffee {state.shopName ? `from ${state.shopName} ` : ''}was already redeemed{when ? ` on ${when}` : ''}.
        </p>
      </>
    );
  }
  if (state.kind === 'expired') {
    const when = formatDay(state.expiresAt);
    return (
      <>
        <h1 className="font-heading text-2xl text-body-grey mb-3">CODE EXPIRED</h1>
        <p className="font-body text-sm text-body-grey">
          This comp {state.shopName ? `from ${state.shopName} ` : ''}expired on {when}. Ask the shop for a new one next time you visit.
        </p>
      </>
    );
  }
  // valid
  const offer = state.offer;
  const shopName = offer.shops?.name ?? null;
  const expiresWhen = formatDay(offer.expires_at);
  return (
    <>
      <h1 className="font-heading text-3xl text-coral mb-2">YOUR FREE COFFEE</h1>
      <p className="font-heading text-xs text-coral mb-6">
        {shopName ? `FROM ${shopName.toUpperCase()}` : 'SHOW THIS TO YOUR BARISTA'}
      </p>
      <div className="pixel-corners-sm border-2 border-coral bg-background p-6 mb-6">
        <code className="font-mono text-2xl md:text-3xl text-primary tracking-widest">
          {offer.code}
        </code>
      </div>
      <p className="font-body text-xs text-body-grey">
        The barista will type this code when stamping your next purchase. Valid until {expiresWhen}.
      </p>
    </>
  );
}
